import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Info } from 'lucide-react'
import type { RegimeTemplate } from '@/hooks/api/useRegimes'
import type { RegimeTemplateListItemDto } from '@/types/api'

// =============================================================================
// Props
// =============================================================================

interface RegimeTemplateSelectorProps {
  templates: (RegimeTemplate | RegimeTemplateListItemDto)[]
  selectedId: string | null
  onSelect: (templateId: string) => void
}

// =============================================================================
// Component
// =============================================================================

export function RegimeTemplateSelector({
  templates,
  selectedId,
  onSelect,
}: RegimeTemplateSelectorProps) {
  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Select Regime Template
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p className="text-sm">
                  Regime templates define TradFi market assumptions (equity, rates, credit) for the scenario.
                  Crypto assets are priced independently based on your BTC path.
                </p>
              </TooltipContent>
            </Tooltip>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Tooltip key={template.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onSelect(template.id)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      selectedId === template.id
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-border hover:border-muted-foreground/50 hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">{template.name}</div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {template.description}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Badge variant="outline" className="text-xs">
                        {template.referencePeriod}
                      </Badge>
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm">
                  <div className="space-y-2">
                    <p className="font-medium">{template.name}</p>
                    <p className="text-sm text-muted-foreground">{template.description}</p>
                    <div className="pt-2 border-t">
                      <p className="text-xs font-medium mb-1">TradFi Assumptions:</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Equity:</span>
                          <br />
                          {template.tradFiAssumptions.equityReturn}%
                        </div>
                        <div>
                          <span className="text-muted-foreground">Rates:</span>
                          <br />
                          {template.tradFiAssumptions.ratesChange}%
                        </div>
                        <div>
                          <span className="text-muted-foreground">Credit:</span>
                          <br />
                          {template.tradFiAssumptions.creditSpread} bps
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground pt-1">
                      Reference: {template.referencePeriod}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
